'use client';
import { Country } from 'country-state-city'; // ✅ Replaced country-list-with-dial-code-and-flag
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { RiDeleteBin6Fill } from 'react-icons/ri';
import { CiEdit } from 'react-icons/ci';
import { LuEye, LuEyeClosed } from 'react-icons/lu';
import apiFetch from '../../utlis/apiFetch';
import { fetchCountries } from '../../redux/features/filterSlice';

const MAX_PASSWORD_LENGTH = 25;
const MAX_PORTAL_NAME_LENGTH = 50;

async function getCountries() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return [];
    let response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/country`,
      {
        method: 'GET',
        headers: {
          authorization: `bearer ${token}`,
        },
      },
    );
    const data = response._data ?? await response.json();
    return data?.body?.countries || [];
  } catch (error) {
    console.error('Error fetching countries:', error);
    return [];
  }
}

async function createCountry({ name, countryCode, dialCode }) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return {};
    let response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/country`,
      {
        method: 'POST',
        body: JSON.stringify({ name, countryCode, dialCode }),
        headers: {
          authorization: `bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    const data = response._data ?? await response.json();
    return data?.body?.country || {};
  } catch (error) {
    console.error('Error creating country:', error);
    return {};
  }
}

async function deleteCountry({ countryId }) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    let response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/country/${countryId}`,
      {
        method: 'DELETE',
        headers: {
          authorization: `bearer ${token}`,
        },
      },
    );
    const data = response._data ?? await response.json();
    return data?.code === 200 ? true : false;
  } catch (error) {
    console.error('Error deleting country:', error);
    return false;
  }
}

async function getProcessTypes() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return [];
    let response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/processtype`,
      {
        method: 'GET',
        headers: {
          authorization: `bearer ${token}`,
        },
      },
    );
    const data = response._data ?? await response.json();
    return data?.body?.processTypes || [];
  } catch (error) {
    console.error('Error fetching process types:', error);
    return [];
  }
}

async function createProcessType({ name }) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return {};
    let response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/processtype`,
      {
        method: 'POST',
        body: JSON.stringify({ name }),
        headers: {
          authorization: `bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    const data = response._data ?? await response.json();
    return data?.body?.processType || {};
  } catch (error) {
    console.error('Error creating process type:', error);
    return {};
  }
}

async function deleteProcessType({ id }) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    let response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/processtype/${id}`,
      {
        method: 'DELETE',
        headers: {
          authorization: `bearer ${token}`,
        },
      },
    );
    const data = response._data ?? await response.json();
    return data?.code === 200 ? true : false;
  } catch (error) {
    console.error('Error deleting process type:', error);
    return false;
  }
}

async function getVisaTypes(countryId) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return [];
    let url = `${process.env.REACT_APP_API_BASE_URL}/api/visatype`;
    if (countryId) {
      url += `?countryId=${countryId}`;
      url += `&isActive=true`;
    }
    let response = await apiFetch(url, {
      method: 'GET',
      headers: {
        authorization: `bearer ${token}`,
      },
    });
    const data = response._data ?? await response.json();
    return data?.body?.visaTypes || [];
  } catch (error) {
    console.error('Error fetching visa types:', error);
    return [];
  }
}

async function createVisaType({ title, countryId }) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return {};
    let response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/visatype`,
      {
        method: 'POST',
        body: JSON.stringify({ title, countryId }),
        headers: {
          authorization: `bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    const data = response._data ?? await response.json();
    return data?.body?.visaType || {};
  } catch (error) {
    console.error('Error creating visa type:', error);
    return {};
  }
}

async function deleteVisaType({ id }) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    let response = await apiFetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/visatype/${id}`,
      {
        method: 'DELETE',
        headers: {
          authorization: `bearer ${token}`,
        },
      },
    );
    const data = response._data ?? await response.json();
    return data?.code === 200 ? true : false;
  } catch (error) {
    console.error('Error deleting visa type:', error);
    return false;
  }
}

// ✅ Helper: build a lookup map from country-state-city once, reuse everywhere
function buildCountryMap() {
  const all = Country.getAllCountries();
  // keyed by isoCode (e.g. "AU", "US") for O(1) lookups
  return Object.fromEntries(all.map((c) => [c.isoCode, c]));
}

const COUNTRY_MAP = buildCountryMap(); // built once at module level, not on every render

// ✅ Helper: convert a raw API country object into the display shape
function formatCountry(ct) {
  const iso2 = ct.countryCode || '';
  const match = COUNTRY_MAP[iso2];
  return {
    _id: ct._id.toString(),
    name: ct.name,
    code: iso2,
    dial_code: match ? `+${match.phonecode}` : ct.dialCode || '',
    flag: match?.flag || '',               // emoji flag e.g. 🇦🇺
    flagPngUrl: iso2
      ? `https://flagcdn.com/w40/${iso2.toLowerCase()}.png`
      : '',
    flagSvgUrl: iso2
      ? `https://flagcdn.com/${iso2.toLowerCase()}.svg`
      : '',
  };
}

const SettingsPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('section') || 'main';
  const initialVisaCountryId = searchParams.get('countryId') || '';
  const initialVisaCountryName = searchParams.get('countryName') || '';
  const portalSections = ['portal', 'portal-countries', 'portal-visa-category', 'portal-process-type'];

  const getCurrentUserRole = () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return '';
      return JSON.parse(userData)?.role || '';
    } catch {
      return '';
    }
  };

  const currentUserRole = getCurrentUserRole();
  const canAccessPortalSettings = currentUserRole === 'admin' || (currentUserRole === 'sub-admin' && JSON.parse(localStorage.getItem('user'))?.permissions?.includes('documents'));
  const setActiveSection = (section, options = {}) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('section', section);

      if (section === 'portal-visa-category') {
        if (options.countryId) {
          newParams.set('countryId', options.countryId);
        }
        if (options.countryName) {
          newParams.set('countryName', options.countryName);
        }
      } else {
        newParams.delete('countryId');
        newParams.delete('countryName');
      }

      return newParams;
    });
  };

  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    country: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [countries, setCountries] = useState([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [addedCountries, setAddedCountries] = useState([]);
  const [selectedCountryForVisa, setSelectedCountryForVisa] = useState({
    _id: initialVisaCountryId,
    name: initialVisaCountryName,
  });

  const fillUserDate = async () => {
    const data = await localStorage.getItem('user');
    const user = JSON.parse(data);
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        country: user.country || '',
      });
    }
  };

  async function getSetCountryies() {
    setIsLoading(true);
    try {
      const fetchedCountries = await getCountries();
      // ✅ Replaced CountryList.getAll() + manual map with formatCountry helper using COUNTRY_MAP
      const formatted = fetchedCountries.map((ct) => formatCountry(ct));
      setAddedCountries(formatted);
    } catch (err) {
      console.error('Error fetching countries:', err);
    } finally {
      setIsLoading(false);
    }
  }

  const refreshCountriesEverywhere = async () => {
    await getSetCountryies();
    dispatch(fetchCountries());
  };

  useEffect(() => {
    fillUserDate();
    getSetCountryies();
  }, []);

  useEffect(() => {
    if (!canAccessPortalSettings && portalSections.includes(activeSection)) {
      setActiveSection('main');
    }
  }, [activeSection, canAccessPortalSettings]);

  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    maxLength: true,
    hasLetter: false,
    hasNumber: false,
    match: false,
  });

  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  useEffect(() => {
    const newPassword = passwords.newPassword;
    const confirmPassword = passwords.confirmPassword;
    setPasswordStrength({
      length: newPassword.length >= 8,
      maxLength: newPassword.length <= MAX_PASSWORD_LENGTH,
      hasLetter: /[a-zA-Z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
      match: newPassword === confirmPassword && newPassword !== '',
    });
  }, [passwords.newPassword, passwords.confirmPassword]);

  // OTP state management for admin password reset
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [resendDisabled, setResendDisabled] = useState(true);
  const [timer, setTimer] = useState(60);
  const [isOtpLoading, setIsOtpLoading] = useState(false);

  // Timer for OTP resend
  useEffect(() => {
    let interval = null;
    if (otpSent && resendDisabled && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setResendDisabled(false);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpSent, resendDisabled, timer]);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
    setProfileError('');
    setProfileSuccess('');
  };

  const validateProfileForm = () => {
    const errors = {};
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.country.trim()) errors.country = 'Country is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveGeneral = async (e) => {
    e.preventDefault();
    setProfileSuccess('');
    setProfileError('');
    if (!validateProfileForm()) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token not found');
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/updateProfile`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        },
      );
      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to update profile');
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...userData, ...formData }));
      setProfileSuccess('Profile updated successfully!');
      setTimeout(() => {
        setActiveSection('main');
        setProfileSuccess('');
      }, 2000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setProfileError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  // OTP Handler Functions
  const handleSendOTP = async () => {
    setIsOtpLoading(true);
    setOtpError('');
    setOtpSuccess('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/adminForgotPassword`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send OTP');

      setOtpSent(true);
      setOtpSuccess('OTP sent successfully to admin email');
      setResendDisabled(true);
      setTimer(60);
      setTimeout(() => setOtpSuccess(''), 3000);
    } catch (err) {
      console.error('Error sending OTP:', err);
      setOtpError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow digits
    
    const newOtpCode = [...otpCode];
    newOtpCode[index] = value.slice(-1); // Only take last character
    setOtpCode(newOtpCode);
    setOtpError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const code = otpCode.join('');
    if (code.length !== 6) {
      setOtpError('Please enter the complete 6-digit code');
      return;
    }

    setIsOtpLoading(true);
    setOtpError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');

      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/verifyAdminOTP`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ code }),
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || 'Invalid OTP');

      setOtpVerified(true);
      setOtpSuccess('OTP verified successfully! You can now update your password.');
      setTimeout(() => setOtpSuccess(''), 3000);
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setOtpError(err.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleResendOTP = () => {
    setOtpCode(['', '', '', '', '', '']);
    setOtpVerified(false);
    handleSendOTP();
  };

  const validatePassword = () => {
    if (!passwordStrength.length || !passwordStrength.maxLength || !passwordStrength.hasLetter || !passwordStrength.hasNumber) {
      setError(`New password must be 8-${MAX_PASSWORD_LENGTH} characters and contain both letters and numbers`);
      return false;
    }
    if (!passwordStrength.match) {
      setError('New passwords do not match');
      return false;
    }
    if (passwords.oldPassword === passwords.newPassword) {
      setError('New password must be different from the old password');
      return false;
    }
    return true;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    // Check if admin user has verified OTP
    if (currentUserRole === 'admin' && !otpVerified) {
      setError('Please verify OTP before updating password');
      return;
    }

    if (!passwords.oldPassword || !passwords.newPassword || !passwords.confirmPassword) {
      setError('All password fields are required');
      return;
    }
    if (!validatePassword()) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token not found');
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/updatePassword`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            oldPassword: passwords.oldPassword,
            newPassword: passwords.newPassword,
          }),
        },
      );
      const data = response._data ?? await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to update password');
      setSuccess('Password has been updated successfully');
      setPasswords({ oldPassword: '', newPassword: '', confirmPassword: '' });
      
      // Reset OTP state after successful password update
      if (currentUserRole === 'admin') {
        setOtpSent(false);
        setOtpVerified(false);
        setOtpCode(['', '', '', '', '', '']);
        setOtpError('');
        setOtpSuccess('');
        setResendDisabled(true);
        setTimer(60);
      }
      
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Replaced CountryList.getAll() with Country.getAllCountries() from country-state-city
  // Filters out already-added countries, maps to the same display shape used throughout
  useEffect(() => {
    const all = Country.getAllCountries();
    const filtered = all.filter(
      (c) => !addedCountries.some((ac) => ac.code === c.isoCode),
    );
    const formatted = filtered.map((ct) => ({
      _id: ct.isoCode,                          // country-state-city has no _id; isoCode is unique
      name: ct.name,
      code: ct.isoCode,
      dial_code: `+${ct.phonecode}`,
      flag: ct.flag,                             // emoji flag
      flagPngUrl: `https://flagcdn.com/w40/${ct.isoCode.toLowerCase()}.png`,
      flagSvgUrl: `https://flagcdn.com/${ct.isoCode.toLowerCase()}.svg`,
    }));
    setCountries(formatted);
  }, [addedCountries]);

  const handleAdd = () => {
    if (!selectedCode) return;
    setIsLoading(true);
    // ✅ selectedCode is now the country name (same as before in the select value)
    const selected = countries.find((c) => c.name === selectedCode);
    if (!selected) {
      setIsLoading(false);
      return;
    }
    if (addedCountries.some((c) => c.code === selected.code)) {
      setIsLoading(false);
      return;
    }
    createCountry({
      name: selected.name,
      countryCode: selected.code,
      dialCode: selected.dial_code,
    })
      .then(async (created) => {
        if (created && created?.name) {
          await refreshCountriesEverywhere();
          setSelectedCode('');
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  const handleDelete = (countryId) => {
    setIsLoading(true);
    deleteCountry({ countryId })
      .then(async (success) => {
        if (success) {
          await refreshCountriesEverywhere();
        } else {
          alert('Failed to delete country. Please try again.');
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  const [processTypes, setProcessTypes] = useState([]);
  const [newProcessType, setNewProcessType] = useState('');

  const [visaTypes, setVisaTypes] = useState([]);
  const [newVisaType, setNewVisaType] = useState('');
  const [visaSuggestions, setVisaSuggestions] = useState([]);
  const [isVisaSuggestionOpen, setIsVisaSuggestionOpen] = useState(false);

  const visaCategories = [
    { name: 'Visit Visa', key: 'visitvisaApplicationsCount' },
    { name: 'Study Visa', key: 'studyvisaApplicationsCount' },
    { name: '485 Visa', key: 'four85visaApplicationsCount' },
    { name: 'Permanent Residence', key: 'permanentResidenceApplicationsCount' },
    { name: 'Spouse Visa', key: 'spousevisaApplicationsCount' },
    { name: 'GSM ( 491,190,189) Visa', key: 'gsmvisaApplicationsCount' },
    { name: 'Protection', key: 'protectionvisaApplicationsCount' },
    { name: 'ART Visa', key: 'aatvisaApplicationsCount' },
    { name: 'FCC Appeals', key: 'fcAppealsApplicationsCount' },
    { name: 'ES Visa', key: 'esVisaApplicationsCount' },
    { name: 'Skill Assesment', key: 'skillAssesementApplicationsCount' },
  ];

  const getVisibleVisaCategories = () => {
    if (!selectedCountryForVisa || !selectedCountryForVisa.name) return visaCategories;
    const countryName = selectedCountryForVisa.name;
    const tier1 = ['Australia'];
    const tier2 = ['USA', 'UK', 'Europe', 'Canada'];
    const tier3 = ['New Zealand', 'Japan', 'UAE'];
    if (tier1.includes(countryName)) return visaCategories;
    if (tier2.includes(countryName)) return visaCategories.slice(0, 2);
    if (tier3.includes(countryName)) return visaCategories.slice(0, 1);
    return visaCategories;
  };

  const getSetVisaTypes = (countryId) => {
    setIsLoading(true);
    getVisaTypes(countryId)
      .then((data) => {
        setVisaTypes(data || []);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching visa types:', err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (activeSection === 'portal-visa-category' && selectedCountryForVisa?._id) {
      getSetVisaTypes(selectedCountryForVisa._id);
      setNewVisaType('');
      setVisaSuggestions([]);
    }
  }, [activeSection, selectedCountryForVisa]);

  useEffect(() => {
    if (activeSection !== 'portal-visa-category') return;

    const countryIdFromQuery = searchParams.get('countryId') || '';
    const countryNameFromQuery = searchParams.get('countryName') || '';

    if (!countryIdFromQuery) return;

    if (
      countryIdFromQuery !== selectedCountryForVisa?._id ||
      countryNameFromQuery !== selectedCountryForVisa?.name
    ) {
      setSelectedCountryForVisa({
        _id: countryIdFromQuery,
        name: countryNameFromQuery,
      });
    }
  }, [activeSection, searchParams, selectedCountryForVisa]);

  const handleVisaTypeInputChange = (e) => {
    const value = e.target.value.slice(0, MAX_PORTAL_NAME_LENGTH);
    setNewVisaType(value);
    if (value.trim()) {
      const visible = getVisibleVisaCategories();
      const filtered = visible.filter((cat) =>
        cat.name.toLowerCase().includes(value.toLowerCase()),
      );
      setVisaSuggestions(filtered);
      setIsVisaSuggestionOpen(true);
    } else {
      setVisaSuggestions([]);
      setIsVisaSuggestionOpen(false);
    }
  };

  const [isVisaAdding, setIsVisaAdding] = useState(false);

  const handleAddVisaType = (title) => {
    const typeToAdd = (title || newVisaType).trim().slice(0, MAX_PORTAL_NAME_LENGTH);
    if (!typeToAdd || !selectedCountryForVisa?._id) return;
    setIsVisaAdding(true);
    setIsLoading(true);
    if (visaTypes.some((vt) => vt.title.toLowerCase() === typeToAdd.toLowerCase())) {
      setIsLoading(false);
      setIsVisaAdding(false);
      setNewVisaType('');
      setIsVisaSuggestionOpen(false);
      return;
    }
    createVisaType({ title: typeToAdd, countryId: selectedCountryForVisa._id })
      .then((created) => {
        if (created) {
          if (created._id) {
            setVisaTypes((prev) => [...prev, created]);
          } else {
            getSetVisaTypes(selectedCountryForVisa._id);
          }
          setNewVisaType('');
          setIsVisaSuggestionOpen(false);
        }
        setIsLoading(false);
        setIsVisaAdding(false);
      })
      .catch(() => {
        setIsLoading(false);
        setIsVisaAdding(false);
      });
  };

  const handleDeleteVisaType = (id) => {
    setIsLoading(true);
    deleteVisaType({ id })
      .then((success) => {
        if (success) {
          setVisaTypes((prev) => prev.filter((vt) => vt._id !== id));
        } else {
          alert('Failed to delete visa type. Please try again.');
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  const getSetProcessTypes = () => {
    setIsLoading(true);
    getProcessTypes()
      .then((data) => {
        setProcessTypes(data || []);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching process types:', err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (activeSection === 'portal-process-type') {
      getSetProcessTypes();
    }
  }, [activeSection]);

  const handleAddProcessType = () => {
    const processTypeToAdd = newProcessType.trim().slice(0, MAX_PORTAL_NAME_LENGTH);
    if (!processTypeToAdd) return;
    setIsLoading(true);
    if (processTypes.some((pt) => pt.name.toLowerCase() === processTypeToAdd.toLowerCase())) {
      setIsLoading(false);
      return;
    }
    createProcessType({ name: processTypeToAdd })
      .then((created) => {
        if (created && created.name) {
          setProcessTypes((prev) => [...prev, created]);
          setNewProcessType('');
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  const handleDeleteProcessType = (id) => {
    setIsLoading(true);
    deleteProcessType({ id })
      .then((success) => {
        if (success) {
          setProcessTypes((prev) => prev.filter((pt) => pt._id !== id));
        } else {
          alert('Failed to delete process type. Please try again.');
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  if (activeSection === 'main') {
    return (
      <div className="max-w-2xl p-4 md:p-10 text-sm md:text-base">
        <h1 className="text-xl md:text-3xl font-normal mb-8">Settings</h1>
        <div className="space-y-0">
          {canAccessPortalSettings && (
            <div
              className="flex items-center justify-between py-5 border-b cursor-pointer"
              onClick={() => setActiveSection('portal')}
            >
              <h2 className="text-base md:text-lg font-normal">Portal Settings</h2>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
          <div
            className="flex items-center justify-between py-5 border-b cursor-pointer"
            onClick={() => setActiveSection('general')}
          >
            <h2 className="text-base md:text-lg font-normal">General Settings</h2>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div
            className="flex items-center justify-between py-5 border-b cursor-pointer"
            onClick={() => setActiveSection('security')}
          >
            <h2 className="text-base md:text-lg font-normal">Security Settings</h2>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  if (activeSection === 'portal') {
    return (
      <div className="max-w-2xl p-4 md:p-10 text-sm md:text-base">
        <button
          type="button"
          onClick={() => setActiveSection('main')}
          className="mb-6 text-[#036494] font-medium flex items-center gap-2 hover:underline"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-xl md:text-3xl font-normal mb-8">Portal Settings</h1>
        <div className="space-y-0">
          <div
            className="flex items-center justify-between py-5 border-b cursor-pointer"
            onClick={() => setActiveSection('portal-countries')}
          >
            <h2 className="text-base md:text-lg font-normal">Countries</h2>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </div>
          <div
            className="flex items-center justify-between py-5 border-b cursor-pointer"
            onClick={() => setActiveSection('portal-process-type')}
          >
            <h2 className="text-base md:text-lg font-normal">Process Type</h2>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  if (activeSection === 'portal-countries') {
    return (
      <div className="p-4 sm:p-8 min-h-screen">
        <button
          type="button"
          onClick={() => setActiveSection('portal')}
          className="mb-6 text-[#036494] font-medium flex items-center gap-2 hover:underline"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-3xl font-semibold mb-6">Countries</h1>
        <span className="mb-4 text-sm">Select Country OR Enter Country Name to add below</span>
        <div className="flex flex-col sm:flex-row gap-4 mb-8 w-full max-w-4xl mt-2">
          <select
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
            className="w-full flex-1 p-3 rounded-lg border border-gray-300 shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#036494]"
          >
            <option value="" disabled>Select Country</option>
            {countries.map((country, idx) => (
              <option key={`${country.code}-${idx}`} value={country.name}>
                {country.name}
              </option>
            ))}
          </select>
          <button
            onClick={handleAdd}
            disabled={isLoading}
            className="w-full sm:w-auto px-6 sm:px-16 py-3 bg-[#036494] text-white rounded-lg shadow-md hover:bg-[#036494]/80 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="w-full px-0 sm:px-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {addedCountries.map((country, idx) => (
                <div
                  key={`${country._id}-${idx}`}
                  className="flex items-center justify-between bg-white rounded-xl shadow-lg px-4 sm:px-5 py-4 sm:py-6 border w-full"
                >
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className="flex-shrink-0">
                      {/* ✅ Still using flagcdn.com SVG URLs — same as before, no visual change */}
                      <img
                        src={country.flagSvgUrl}
                        alt={country.name}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover drop-shadow-lg"
                      />
                    </div>
                    <div className="font-medium text-sm sm:text-base truncate">{country.name}</div>
                  </div>
                  <div className="flex items-center justify-center gap-3 sm:gap-4 flex-shrink-0">
                    <button
                      onClick={() => {
                        setSelectedCountryForVisa({ _id: country._id, name: country.name });
                        setVisaTypes([]);
                        setActiveSection('portal-visa-category', {
                          countryId: country._id,
                          countryName: country.name,
                        });
                      }}
                      disabled={isLoading}
                      className="text-gray-600 hover:text-gray-800 disabled:cursor-not-allowed transition"
                    >
                      <CiEdit className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                    <button
                      onClick={() => handleDelete(country._id)}
                      disabled={isLoading}
                      className="text-red-500 hover:text-red-700 disabled:cursor-not-allowed transition"
                    >
                      <RiDeleteBin6Fill className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (activeSection === 'portal-visa-category') {
    const visible = getVisibleVisaCategories();
    const existingTitles = visaTypes.map((vt) => vt.title.toLowerCase());
    const availableCategories = visible.filter(
      (c) => !existingTitles.includes(c.name.toLowerCase()),
    );
    const suggestions = newVisaType.trim()
      ? availableCategories.filter((c) =>
          c.name.toLowerCase().includes(newVisaType.toLowerCase()),
        )
      : availableCategories;

    return (
      <div className="p-4 sm:p-8 min-h-screen" onClick={() => setIsVisaSuggestionOpen(false)}>
        <button
          onClick={() => setActiveSection('portal-countries')}
          className="mb-6 text-[#036494] font-medium flex items-center gap-2 hover:underline"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Back to Countries
        </button>

        <h1 className="text-3xl font-semibold mb-2">Visa Categories</h1>
        <h2 className="text-xl text-gray-600 mb-6">for {selectedCountryForVisa?.name}</h2>
        <span className="mb-4 text-sm block">Enter Visa Category to add below</span>

        <div className="w-full max-w-4xl mb-8 relative flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative w-full">
            <input
              value={newVisaType}
              onChange={(e) => {
                setNewVisaType(e.target.value.slice(0, MAX_PORTAL_NAME_LENGTH));
                setIsVisaSuggestionOpen(true);
              }}
              onFocus={() => setIsVisaSuggestionOpen(true)}
              onClick={(e) => {
                e.stopPropagation();
                setIsVisaSuggestionOpen(true);
              }}
              placeholder="Visa category name"
              maxLength={MAX_PORTAL_NAME_LENGTH}
              className="w-full p-3 rounded-lg border border-gray-300 shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#036494]"
            />
            {isVisaSuggestionOpen && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto z-10">
                {suggestions.map((cat, idx) => (
                  <div
                    key={idx}
                    className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-0 text-sm sm:text-base"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNewVisaType(cat.name);
                      setIsVisaSuggestionOpen(false);
                    }}
                  >
                    {cat.name}
                  </div>
                ))}
                {newVisaType && !suggestions.some((s) => s.name.toLowerCase() === newVisaType.toLowerCase()) && (
                  <div
                    className="p-3 hover:bg-gray-100 cursor-pointer text-[#036494] font-medium text-sm sm:text-base"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsVisaSuggestionOpen(false);
                    }}
                  >
                    Add "{newVisaType}" to the list
                  </div>
                )}
                {suggestions.length === 0 && !newVisaType && (
                  <div className="p-3 text-gray-500 italic text-sm sm:text-base">
                    Start typing to see suggestions...
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => handleAddVisaType(newVisaType)}
            disabled={isLoading || isVisaAdding}
            className="w-full sm:w-auto px-6 sm:px-16 bg-[#036494] text-white rounded-lg shadow-md hover:bg-[#036494]/80 transition h-[50px] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isVisaAdding ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Adding...
              </>
            ) : 'Add'}
          </button>
        </div>

        {isLoading && visaTypes.length === 0 ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {visaTypes.map((vt, idx) => (
              <div
                key={`${vt._id}-${idx}`}
                className="flex items-center justify-between bg-white rounded-xl shadow-lg px-5 py-6 border"
              >
                <div className="flex items-center gap-4">
                  <div className="font-medium">{vt.title}</div>
                </div>
                <button
                  onClick={() => handleDeleteVisaType(vt._id)}
                  disabled={isLoading}
                  className="text-red-500 hover:text-red-700 disabled:cursor-not-allowed transition"
                >
                  <RiDeleteBin6Fill className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (activeSection === 'portal-process-type') {
    return (
      <div className="p-4 sm:p-8 min-h-screen">
        <button
          type="button"
          onClick={() => setActiveSection('portal')}
          className="mb-6 text-[#036494] font-medium flex items-center gap-2 hover:underline"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-3xl font-semibold mb-6">Process Type</h1>
        <span className="mb-4 text-sm">Enter Category to add below</span>
        <div className="flex flex-col sm:flex-row gap-4 mb-8 w-full max-w-4xl mt-2">
          <input
            value={newProcessType}
            onChange={(e) =>
              setNewProcessType(e.target.value.slice(0, MAX_PORTAL_NAME_LENGTH))
            }
            placeholder="Process type name"
            maxLength={MAX_PORTAL_NAME_LENGTH}
            className="w-full flex-1 p-3 rounded-lg border border-gray-300 shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#036494]"
          />
          <button
            onClick={handleAddProcessType}
            disabled={isLoading}
            className="w-full sm:w-auto px-6 sm:px-16 py-3 bg-[#036494] text-white rounded-lg shadow-md hover:bg-[#036494]/80 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[200px]">
            <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {processTypes.map((pt, idx) => (
                <div
                  key={`${pt._id}-${idx}`}
                  className="flex items-center justify-between bg-white rounded-xl shadow-lg px-4 sm:px-5 py-4 sm:py-6 border w-full"
                >
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className="font-medium text-sm sm:text-base truncate">{pt.name}</div>
                  </div>
                  <button
                    onClick={() => handleDeleteProcessType(pt._id)}
                    disabled={isLoading}
                    className="text-red-500 hover:text-red-700 disabled:cursor-not-allowed transition flex-shrink-0"
                  >
                    <RiDeleteBin6Fill className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (activeSection === 'general') {
    return (
      <div className="max-w-2xl p-4 md:p-10 text-sm md:text-base">
        <h1 className="text-xl md:text-3xl font-normal mb-8">General Settings</h1>
        {profileSuccess && (
          <div className="bg-green-50 text-green-600 p-3 rounded-md mb-4">{profileSuccess}</div>
        )}
        {profileError && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">{profileError}</div>
        )}
        <form onSubmit={handleSaveGeneral}>
          <div className="mb-8">
            <h2 className="text-base md:text-lg font-normal mb-4">Personal Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1">First Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`w-full p-2 border ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'} rounded bg-gray-50`}
                  required
                />
                {formErrors.firstName && <p className="text-red-500 text-xs mt-1">{formErrors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm mb-1">Last Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`w-full p-2 border ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'} rounded bg-gray-50`}
                  required
                />
                {formErrors.lastName && <p className="text-red-500 text-xs mt-1">{formErrors.lastName}</p>}
              </div>
              <div>
                <label className="block text-sm mb-1">Phone Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full p-2 border ${formErrors.phone ? 'border-red-500' : 'border-gray-300'} rounded bg-gray-50`}
                  required
                />
                {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
              </div>
              <div>
                <label className="block text-sm mb-1">Country <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`w-full p-2 border ${formErrors.country ? 'border-red-500' : 'border-gray-300'} rounded bg-gray-50`}
                  required
                />
                {formErrors.country && <p className="text-red-500 text-xs mt-1">{formErrors.country}</p>}
              </div>
            </div>
          </div>
          <div className="flex justify-between px-2">
            <button
              type="button"
              onClick={() => setActiveSection('main')}
              className="py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full transition-colors"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`py-3 px-4 bg-[#005787] text-white rounded-full transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#004a70]'}`}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  if (activeSection === 'security') {
    return (
      <div className="max-w-2xl p-4 md:p-10 text-sm md:text-base">
        <h1 className="text-xl md:text-3xl font-normal mb-8">Security Settings</h1>
        <div className="mb-8">
          <h2 className="text-base md:text-lg font-normal mb-4">Change your Password</h2>
          <div className="border-t"></div>
        </div>
        <div className="mb-8">
          <h2 className="text-base md:text-lg font-normal mb-4">Reset Password</h2>
          {error && <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">{error}</div>}
          {success && <div className="bg-green-50 text-green-600 p-3 rounded-md mb-4">{success}</div>}
          
          {/* Admin OTP Flow */}
          {currentUserRole === 'admin' && !otpVerified && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 mb-2">
                  🔒 <strong>Admin Security:</strong> For your security, please verify your identity with an OTP sent to the admin email before changing your password.
                </p>
              </div>

              {!otpSent ? (
                <button
                  type="button"
                  onClick={handleSendOTP}
                  disabled={isOtpLoading}
                  className={`w-full py-3 px-4 bg-[#005787] text-white rounded-lg transition-colors ${
                    isOtpLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#004a70]'
                  }`}
                >
                  {isOtpLoading ? 'Sending...' : 'Send OTP to Admin Email'}
                </button>
              ) : (
                <div className="space-y-4">
                  {otpSuccess && <div className="bg-green-50 text-green-600 p-3 rounded-md text-sm">{otpSuccess}</div>}
                  {otpError && <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">{otpError}</div>}
                  
                  <div>
                    <label className="block text-sm mb-2 font-medium">Enter 6-Digit OTP</label>
                    <div className="flex gap-2 justify-center mb-4">
                      {otpCode.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          maxLength="1"
                          value={digit}
                          onChange={(e) => handleOTPChange(index, e.target.value)}
                          onKeyDown={(e) => handleOTPKeyDown(index, e)}
                          className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-[#005787] focus:outline-none"
                          disabled={isOtpLoading}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleVerifyOTP}
                      disabled={isOtpLoading || otpCode.join('').length !== 6}
                      className={`flex-1 py-3 px-4 bg-[#005787] text-white rounded-lg transition-colors ${
                        isOtpLoading || otpCode.join('').length !== 6
                          ? 'opacity-70 cursor-not-allowed'
                          : 'hover:bg-[#004a70]'
                      }`}
                    >
                      {isOtpLoading ? 'Verifying...' : 'Verify OTP'}
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendDisabled || isOtpLoading}
                      className={`py-3 px-4 border-2 border-[#005787] text-[#005787] rounded-lg transition-colors ${
                        resendDisabled || isOtpLoading
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-[#005787] hover:text-white'
                      }`}
                    >
                      {resendDisabled ? `Resend (${timer}s)` : 'Resend OTP'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Password Form - Show for non-admin OR admin with verified OTP */}
          {(currentUserRole !== 'admin' || otpVerified) && (
            <form onSubmit={handleResetPassword}>
              <div className="space-y-4 mb-8">
                <div>
                  <label className="block text-sm mb-1">Enter current password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type={showPassword.oldPassword ? 'text' : 'password'}
                      name="oldPassword"
                      value={passwords.oldPassword}
                      onChange={handlePasswordChange}
                      className="w-full p-2 pr-10 border border-gray-300 rounded bg-gray-50"
                      maxLength={MAX_PASSWORD_LENGTH}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => ({ ...prev, oldPassword: !prev.oldPassword }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                      aria-label={showPassword.oldPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword.oldPassword ? <LuEye className="w-4 h-4" /> : <LuEyeClosed className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-1">Enter new password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type={showPassword.newPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={passwords.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full p-2 pr-10 border border-gray-300 rounded bg-gray-50"
                      minLength={8}
                      maxLength={MAX_PASSWORD_LENGTH}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => ({ ...prev, newPassword: !prev.newPassword }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                      aria-label={showPassword.newPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword.newPassword ? <LuEye className="w-4 h-4" /> : <LuEyeClosed className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwords.newPassword && (
                    <div className="mt-1 space-y-1">
                      <div className="text-xs text-red-500 space-y-0.5">
                        {!passwordStrength.length && <p className="transition-all duration-200">• Password must be at least 8 characters</p>}
                        {!passwordStrength.maxLength && <p className="transition-all duration-200">• Password must be at most 25 characters</p>}
                        {!passwordStrength.hasLetter && <p className="transition-all duration-200">• Password must contain letters</p>}
                        {!passwordStrength.hasNumber && <p className="transition-all duration-200">• Password must contain numbers</p>}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm mb-1">Confirm new password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type={showPassword.confirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={passwords.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full p-2 pr-10 border border-gray-300 rounded bg-gray-50"
                      minLength={8}
                      maxLength={MAX_PASSWORD_LENGTH}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => ({ ...prev, confirmPassword: !prev.confirmPassword }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-800"
                      aria-label={showPassword.confirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword.confirmPassword ? <LuEye className="w-4 h-4" /> : <LuEyeClosed className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwords.confirmPassword && !passwordStrength.match && (
                    <p className="text-xs text-red-500 mt-1 transition-all duration-200">• Passwords do not match</p>
                  )}
                </div>
              </div>
              <div className="flex justify-between px-2">
                <button
                  type="button"
                  onClick={() => setActiveSection('main')}
                  className="py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`py-3 px-4 bg-[#005787] text-white rounded-full transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#004a70]'}`}
                >
                  {isLoading ? 'Updating...' : 'Reset Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default SettingsPage;